/**
 * KnowledgeTooltip — Icône "Où trouver l'information ?" cliquable
 * qui ouvre une popover/modale explicative avec :
 *   - Définition pédagogique
 *   - Instructions "Où trouver" concrètes
 *   - Image explicative (SVG placeholder par défaut)
 *   - Liens vers entrées connexes
 *
 * Usage :
 *   <KnowledgeTooltip entryKey="cref" />
 *   <KnowledgeTooltip entryKey="bacs" size="sm" inline />
 */
import { useState, useEffect } from 'react';
import { Info, HelpCircle, ExternalLink, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { cn } from '@/lib/utils';

// Cache en mémoire — évite de fetch la même entrée 15 fois par page
const cache = new Map();
let listPromise = null;

async function preloadAll() {
    if (cache.size > 0) return cache;
    if (!listPromise) {
        listPromise = api.get('/knowledge-base').then((r) => {
            (r.data?.entries || []).forEach((e) => cache.set(e.key, e));
            return cache;
        }).catch(() => cache);
    }
    return listPromise;
}

const CATEGORY_STYLE = {
    reglementaire: { label: 'Réglementaire', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
    metrique:      { label: 'Métrique',      cls: 'bg-purple-100 text-purple-800 border-purple-200' },
    terrain:       { label: 'Terrain',       cls: 'bg-amber-100 text-amber-800 border-amber-200' },
};

export default function KnowledgeTooltip({
    entryKey,
    inline = false,       // vrai = pas de bouton visible, juste icône stylisée
    label,                // label optionnel remplaçant le titre de l'entrée
    size = 'sm',          // 'xs' | 'sm' | 'md'
    variant = 'ghost',    // 'ghost' | 'chip'
    className,
}) {
    const [open, setOpen] = useState(false);
    const [entry, setEntry] = useState(null);
    const [loading, setLoading] = useState(false);

    // Précharge asynchrone dès le montage (une seule fois pour toute la page)
    useEffect(() => {
        let cancelled = false;
        preloadAll().then((c) => {
            if (!cancelled) setEntry(c.get(entryKey) || null);
        });
        return () => { cancelled = true; };
    }, [entryKey]);

    const openDialog = async () => {
        setOpen(true);
        if (!entry) {
            setLoading(true);
            try {
                const r = await api.get(`/knowledge-base/${entryKey}`);
                cache.set(entryKey, r.data);
                setEntry(r.data);
            } catch (e) { /* silencieux */ }
            finally { setLoading(false); }
        }
    };

    const iconSize = size === 'xs' ? 'w-3 h-3' : size === 'md' ? 'w-5 h-5' : 'w-3.5 h-3.5';
    const btnBase = variant === 'chip'
        ? 'inline-flex items-center gap-1 rounded-full border border-purple-200 bg-purple-50 hover:bg-purple-100 text-purple-800 px-1.5 py-0.5 text-[10px] font-medium'
        : 'inline-flex items-center gap-0.5 text-slate-400 hover:text-purple-600 transition-colors';

    return (
        <>
            <button
                type="button"
                onClick={openDialog}
                title={entry?.title || label || "Où trouver l'information ?"}
                aria-label={`Aide : ${entry?.title || entryKey}`}
                className={cn(btnBase, className)}
                data-testid={`kb-tip-${entryKey}`}
            >
                <Info className={iconSize} />
                {variant === 'chip' && <span>{label || 'Aide'}</span>}
            </button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid={`kb-dialog-${entryKey}`}>
                    {loading || !entry ? (
                        <div className="p-6 text-center text-sm text-slate-500">Chargement…</div>
                    ) : (
                        <>
                            <DialogHeader className="px-5 pt-5 pb-3 bg-gradient-to-br from-purple-50 to-indigo-50 border-b">
                                <div className="flex items-start gap-3">
                                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shrink-0 shadow-sm">
                                        <HelpCircle className="w-5 h-5 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <DialogTitle className="text-base font-bold text-slate-900 leading-snug">
                                            {entry.title}
                                        </DialogTitle>
                                        {entry.category && (
                                            <Badge className={cn('mt-1.5 text-[10px] border', CATEGORY_STYLE[entry.category]?.cls)}>
                                                {CATEGORY_STYLE[entry.category]?.label || entry.category}
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            </DialogHeader>

                            <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
                                {/* Définition */}
                                <div>
                                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Définition</div>
                                    <p className="text-sm text-slate-700 leading-relaxed" data-testid={`kb-definition-${entryKey}`}>
                                        {entry.definition}
                                    </p>
                                </div>

                                {/* Où trouver */}
                                {entry.where_to_find && (
                                    <div>
                                        <div className="text-[11px] font-semibold uppercase tracking-wide text-purple-700 mb-1">
                                            Où trouver l&apos;information ?
                                        </div>
                                        <div className="rounded-lg bg-purple-50 border border-purple-200 px-3 py-2 text-sm text-slate-800" data-testid={`kb-where-${entryKey}`}>
                                            {entry.where_to_find}
                                        </div>
                                    </div>
                                )}

                                {/* Image explicative (SVG placeholder ou upload à venir) */}
                                {entry.image_path && (
                                    <div>
                                        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                                            Visuel
                                        </div>
                                        <div className="rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
                                            <img
                                                src={entry.image_path.startsWith('/api')
                                                    ? `${process.env.REACT_APP_BACKEND_URL}${entry.image_path}`
                                                    : entry.image_path}
                                                alt={entry.title}
                                                className="w-full h-auto"
                                                data-testid={`kb-image-${entryKey}`}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Liens vers entrées connexes */}
                                {entry.related_keys?.length > 0 && (
                                    <div>
                                        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Notions liées</div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {entry.related_keys.map((k) => (
                                                <RelatedChip key={k} entryKey={k} onOpen={(newKey) => {
                                                    // Recharge la nouvelle entrée dans le même dialog
                                                    const cached = cache.get(newKey);
                                                    if (cached) setEntry(cached);
                                                    else {
                                                        setLoading(true);
                                                        api.get(`/knowledge-base/${newKey}`).then((r) => {
                                                            cache.set(newKey, r.data);
                                                            setEntry(r.data);
                                                        }).finally(() => setLoading(false));
                                                    }
                                                }} />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}

function RelatedChip({ entryKey, onOpen }) {
    const [title, setTitle] = useState(entryKey);
    useEffect(() => {
        preloadAll().then((c) => {
            const e = c.get(entryKey);
            if (e?.title) setTitle(e.title);
        });
    }, [entryKey]);
    return (
        <button
            type="button"
            onClick={() => onOpen(entryKey)}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white border border-slate-200 text-xs text-slate-700 hover:border-purple-300 hover:text-purple-700 hover:bg-purple-50 transition-colors"
            data-testid={`kb-related-${entryKey}`}
        >
            <ChevronRight className="w-3 h-3" />
            <span>{title}</span>
        </button>
    );
}

/**
 * Image explicative — fetch authentifié (blob URL) car l'endpoint /api/knowledge-base/{key}/image
 * nécessite le Bearer token, ce que les <img src> naïfs ne fournissent pas.
 */
function KnowledgeImage({ entryKey, alt }) {
    const [url, setUrl] = useState(null);
    const [error, setError] = useState(false);
    useEffect(() => {
        let cancelled = false;
        let objectUrl = null;
        (async () => {
            try {
                const res = await api.get(`/knowledge-base/${entryKey}/image`, { responseType: 'blob' });
                if (cancelled) return;
                objectUrl = URL.createObjectURL(res.data);
                setUrl(objectUrl);
            } catch { if (!cancelled) setError(true); }
        })();
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [entryKey]);
    if (error) return <div className="p-4 text-xs text-slate-400 text-center">Image indisponible</div>;
    if (!url) return <div className="p-4 text-xs text-slate-400 text-center">Chargement…</div>;
    return (
        <img
            src={url}
            alt={alt}
            className="w-full h-auto block"
            data-testid={`kb-image-${entryKey}`}
        />
    );
}
